import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { IMcpOAuthAuthorization } from "@common/identity/mcp-oauth-authorization.interface";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { StakeholderServicesService } from "app/features/tools/stakeholder-services.service";
import { from } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-mcp-oauth-authorizations",
    templateUrl: "./mcp-oauth-authorizations.component.html",
    standalone: false,
})
export class McpOAuthAuthorizationsComponent implements OnChanges {
    @Input() public person?: Person;

    public authorizations?: IMcpOAuthAuthorization[];
    public errorMessage?: string;

    public constructor(
        private stakeholderServices: StakeholderServicesService,
        private dialogService: AdaptCommonDialogService,
    ) { }

    public async ngOnChanges(changes: SimpleChanges) {
        if (changes.person) {
            await this.loadAuthorizations();
        }
    }

    @Autobind
    public revokeAuthorization(authorization: IMcpOAuthAuthorization) {
        return this.dialogService.openConfirmationDialog({
            title: "Revoke application access",
            message: `<p>This application will no longer be able to access Lumia on this user's behalf.</p>
                      <p>The user will need to authorise it again before reconnecting.</p>`,
            confirmButtonText: "Revoke access",
            confirmButtonPreset: "delete",
        }).pipe(
            switchMap(() => from(this.stakeholderServices.revokeMcpOAuthAuthorization(
                this.person!.personId,
                authorization.ClientId,
            ))),
            tap(() => this.authorizations = this.authorizations?.filter((item) => item.ClientId !== authorization.ClientId)),
            catchError((error) => this.dialogService.showErrorDialog(
                "Failed to revoke access",
                ErrorHandlingUtilities.getHttpResponseMessage(error),
            )),
        );
    }

    private async loadAuthorizations() {
        if (!this.person?.userId) {
            this.authorizations = [];
            this.errorMessage = undefined;
            return;
        }

        this.authorizations = undefined;
        try {
            this.authorizations = await this.stakeholderServices.getMcpOAuthAuthorizations(this.person.personId);
            this.errorMessage = undefined;
        } catch (error) {
            this.authorizations = [];
            this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(error);
        }
    }
}
